import React, { useState, useEffect } from 'react';
import { 
  FaBell, 
  FaGraduationCap, 
  FaUsers, 
  FaUserCheck, 
  FaClock, 
  FaArrowUp, 
  FaExclamationTriangle,
  FaHome,
  FaUniversity,
  FaUserPlus,
  FaUserShield,
  FaEye
} from 'react-icons/fa';
import { DashboardService } from '../../services/DashboardService';
import { ApplicantService } from '../../services/ApplicantService';
import { useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';

const SaasOverviewSection = () => {
  const [stats, setStats] = useState({
    opportunities: { total: 0, percentageChange: 0 },
    customUsers: { count: 0 }
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [appStats, setAppStats] = useState({ total: 0, pending: 0 });
  const { userDetails: user } = useContext(AuthContext);

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        setLoading(true);
        const [opportunityStats, customUsersCount, activities, applicantStats] = await Promise.all([
          DashboardService.getOpportunityStats(),
          DashboardService.getCustomUsersCount(),
          DashboardService.getRecentActivities(),
          ApplicantService.getApplicantStats()
        ]);

        setStats({
          opportunities: {
            total: opportunityStats.total_count,
            percentageChange: opportunityStats.percentage_change
          },
          customUsers: {
            count: customUsersCount.count
          }
        });
        setRecentActivities(activities.activities || []);
        setAppStats({
          total: applicantStats.total_count || 0,
          pending: (applicantStats.status_counts?.new || 0)
        });
      } catch (err) {
        console.error('Error fetching dashboard stats:', err);
        setError('Failed to load dashboard statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardStats();
  }, []);

  const formatPercentage = (percentage) => {
    if (percentage === 0) return '0%';
    if (percentage > 0) return `+${percentage.toFixed(1)}%`;
    return `${percentage.toFixed(1)}%`;
  };

  const formatTimeAgo = (timestamp) => {
    const now = Math.floor(Date.now() / 1000);
    const diff = now - timestamp;
    
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    if (diff < 2592000) return `${Math.floor(diff / 86400)} days ago`;
    return `${Math.floor(diff / 2592000)} months ago`;
  };

  const getActivityIcon = (type, description) => {
    if (type === "opportunity") return <FaGraduationCap className="w-4 h-4 text-[#DB4F40]" />;
    if (type === "user") return <FaUserPlus className="w-4 h-4 text-green-500" />;
    if (type === "role") return <FaUserShield className="w-4 h-4 text-purple-500" />;
    if (type === "application") {
      if (description?.toLowerCase().includes("offered")) return <FaUserCheck className="w-4 h-4 text-green-600" />;
      if (description?.toLowerCase().includes("rejected")) return <FaExclamationTriangle className="w-4 h-4 text-red-500" />;
      if (description?.toLowerCase().includes("reviewed")) return <FaEye className="w-4 h-4 text-yellow-500" />;
      return <FaUsers className="w-4 h-4 text-blue-500" />;
    }
    return <FaBell className="w-4 h-4 text-gray-500" />;
  };

  const renderActivityLine = (activity) => {
    // Opportunity published
    if (activity.type === "opportunity") {
      return <><span className="">PhD Opportunity "{activity.title}"</span> was published{activity.created_by && <> by <span className="">{activity.created_by}</span></>}</>;
    }
    // New user
    if (activity.type === "user") {
      return <><span className="">New user "{activity.title}"</span> created {activity.created_by && <> by <span className="">"{activity.created_by}"</span></>}</>;
    }
    // New role
    if (activity.type === "role") {
      return <><span className="">{activity.title}</span> role was created {activity.created_by && <> by <span className="">"{activity.created_by}"</span></>}</>;
    }
    // Application events
    if (activity.type === "application") {
      if (activity.description.toLowerCase().includes("offered")) {
        return <><span className="">PhD Opportunity "{activity.title}"</span> was <span className="">offered</span>{activity.created_by && <> by <span className="">"{activity.created_by}"</span></>}</>;
      }
      if (activity.description.toLowerCase().includes("rejected")) {
        return <><span className="">PhD Opportunity "{activity.title}"</span> was <span className="">rejected</span>{activity.created_by && <> by <span className="">{activity.created_by}</span></>}</>;
      }
      if (activity.description.toLowerCase().includes("reviewed")) {
        return <><span>  {activity.title}</span> was reviewed {activity.created_by && <> by <span className="">{activity.created_by}</span></>}</>;
      }
      // New application
      return <><span className=""> {activity.description} </span> </>;
    }
    // Fallback
    return <span>{activity.description}: <span className="">{activity.title}</span></span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFEEED] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#DB4F40] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#1B222A]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-[#DB4F40]">Dashboard Overview</h1>
      </div>

      {/* Welcome Message */}
      <div className="bg-gradient-to-r from-[#FFEEED] to-white rounded-2xl p-6 border-2 border-[#FFD6D4] shadow-lg">
        <h2 className="text-xl font-semibold text-[#1B222A] mb-2">
          Welcome back, {user?.first_name || 'User'}!
        </h2>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center">
            <FaExclamationTriangle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-700 font-medium">Error Loading Dashboard</span>
          </div>
          <p className="text-red-700 mt-1">{error}</p>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Total Opportunities */}
        <div className="bg-white rounded-2xl shadow-md p-0 flex flex-col items-center justify-center h-40 hover:shadow-xl transition-all duration-300 group relative overflow-hidden">
          <div className="absolute top-4 left-4 flex items-center justify-center w-12 h-12 bg-[#F8E1DE] rounded-xl group-hover:scale-105 transition-transform">
            <FaGraduationCap className="w-7 h-7 text-[#DB4F40]" />
          </div>
          <div className="flex-1 flex flex-col items-center justify-center w-full h-full">
            <span className="block text-4xl font-extrabold text-[#DB4F40] mb-2 mt-2">{stats.opportunities.total}</span>
            <span className="font-semibold text-base text-[#1B222A] tracking-tight">Total Opportunities</span>
          </div>
        </div>

        {/* Active Applications */}
        <div className="bg-white rounded-2xl shadow-md p-0 flex flex-col items-center justify-center h-40 hover:shadow-xl transition-all duration-300 group relative overflow-hidden">
          <div className="absolute top-4 left-4 flex items-center justify-center w-12 h-12 bg-[#E6F7F1] rounded-xl group-hover:scale-105 transition-transform">
            <FaUsers className="w-7 h-7 text-[#1BC17B]" />
          </div>
          <div className="flex-1 flex flex-col items-center justify-center w-full h-full">
            <span className="block text-4xl font-extrabold text-[#1BC17B] mb-2 mt-2">{appStats.total}</span>
            <span className="font-semibold text-base text-[#1B222A] tracking-tight">Active Applications</span>
          </div>
        </div>

        {/* Pending Reviews */}
        <div className="bg-white rounded-2xl shadow-md p-0 flex flex-col items-center justify-center h-40 hover:shadow-xl transition-all duration-300 group relative overflow-hidden">
          <div className="absolute top-4 left-4 flex items-center justify-center w-12 h-12 bg-[#FFF4E5] rounded-xl group-hover:scale-105 transition-transform">
            <FaClock className="w-7 h-7 text-[#FFA726]" />
          </div>
          <div className="flex-1 flex flex-col items-center justify-center w-full h-full">
            <span className="block text-4xl font-extrabold text-[#FFA726] mb-2 mt-2">{appStats.pending}</span>
            <span className="font-semibold text-base text-[#1B222A] tracking-tight">Pending Reviews</span>
          </div>
        </div>
      </div>

      {/* Recent Activities */}
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border-2 border-[#FFD6D4] overflow-hidden">
        <div className="p-6 border-b-2 border-[#FFD6D4] bg-gradient-to-r from-[#FFEEED] to-white">
          <h2 className="text-xl font-semibold text-[#1B222A]">Recent Activities</h2>
        </div>
        <div className="p-6">
          {recentActivities.length > 0 ? (
            <div className="space-y-2">
              {recentActivities.map((activity, index) => (
                <div key={activity.id} className="flex items-center px-2 py-2 rounded-lg hover:bg-[#FFEEED]/60 transition-colors duration-150 text-sm">
                  {/* Icon */}
                  <span className="mr-3 flex-shrink-0">
                    {getActivityIcon(activity.type, activity.description)}
                  </span>
                  {/* Description */}
                  <span className="flex-1">
                    {renderActivityLine(activity)}
                  </span>
                  {/* Time */}
                  <span className="ml-3 text-xs text-[#7B7F87] flex-shrink-0">
                    {formatTimeAgo(activity.created_at)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <FaBell className="w-8 h-8 text-[#1B222A]/40 mx-auto mb-2" />
              <p className="text-[#1B222A]/60">No recent activities</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SaasOverviewSection;